import React, { useCallback } from "react";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { TabPanelProps } from "@mui/lab/TabPanel/index";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Tab from "@mui/material/Tab";
import styled from "styled-components";

import { StandardButton } from "../StandardButton";
import TabPanelContent from "./TabPanelContent";

const StyledDialogTitle = styled(DialogTitle)`
    padding-bottom: 0;
`;

const StyledTabList = styled(TabList)`
    .MuiTabs-indicator {
        display: flex;
        justify-content: center;
        background-color: transparent;
    }
`;

const StyledTab = styled(Tab)`
    &.MuiTab-textColorPrimary {
        color: var(--formant-color-primary-silver);
    }
    &.Mui-selected {
        color: #fff;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    background: var(--formant-color-primary-steel01);
`;

const StyledTabIndicatorUnderline = styled.span`
    width: 80%;
    background-color: var(--formant-color-highlights-blue);
`;

const StyledStandardButton = styled(StandardButton)`
    width: 6.125rem;
`;

export interface IDialogOptions {
    tabLabel: string;
    tabValue: string;
    tabContent: React.ReactNode;
    tabPanelProps?: Omit<TabPanelProps, "value">;
}

interface IDialogWithTabsProps {
    dialogOptions: IDialogOptions[];
    dialogTitle: string;
    dialogProps: DialogProps;
    defaultTabValue: string;
    isLoading?: boolean;
    onCancel: () => void;
    onSave: (tabValue: string) => void;
}

export default function DialogWithTabs({
    dialogProps,
    dialogOptions,
    dialogTitle,
    defaultTabValue,
    isLoading,
    onCancel,
    onSave,
}: IDialogWithTabsProps) {
    const [value, setValue] = React.useState(defaultTabValue);

    const handleChange = (_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const onClickSave = useCallback(() => {
        onSave(value);
    }, [value, onSave]);

    return (
        <Dialog {...dialogProps}>
            <StyledDialogTitle>
                <p>{dialogTitle}</p>
            </StyledDialogTitle>

            <Box sx={{ width: "100%" }}>
                <TabContext value={value}>
                    <Box
                        sx={{
                            marginLeft: "0.625rem",
                        }}
                    >
                        <StyledTabList
                            onChange={handleChange}
                            aria-label="tabs list"
                            TabIndicatorProps={{
                                children: <StyledTabIndicatorUnderline />,
                            }}
                        >
                            {dialogOptions.map(option => (
                                <StyledTab
                                    key={option.tabValue}
                                    label={option.tabLabel}
                                    value={option.tabValue}
                                />
                            ))}
                        </StyledTabList>
                    </Box>

                    {dialogOptions.map(val => (
                        <TabPanelContent
                            key={val.tabValue}
                            value={val.tabValue}
                            tabPanelProps={val.tabPanelProps}
                        >
                            {val.tabContent}
                        </TabPanelContent>
                    ))}
                </TabContext>
            </Box>

            <StyledDialogActions>
                <StyledStandardButton dark={true} onClick={onCancel}>
                    Cancel
                </StyledStandardButton>

                <StyledStandardButton
                    loading={isLoading}
                    primary={true}
                    onClick={onClickSave}
                >
                    Done
                </StyledStandardButton>
            </StyledDialogActions>
        </Dialog>
    );
}
